/**
 * Debouncer - Delay execution mechanism for save operations
 *
 * Implements debounce logic to batch rapid edits into a single save operation.
 * Only the final state within the debounce window is processed.
 *
 * @module features/storage/debouncer
 * @see SPEC-006-background-cloud-save-queue
 * @see Requirements 2.1, 2.2
 */

/**
 * Options for Debouncer configuration
 */
export interface DebouncerOptions {
  /** Delay in milliseconds before executing callback (default: 2000) */
  delayMs: number;
  /** Optional maximum wait time in milliseconds before forcing execution */
  maxWaitMs?: number;
}

/**
 * Default debounce delay (2 seconds)
 * @see Requirements 2.1 - Debounce save requests with 2-second delay
 */
export const DEFAULT_DEBOUNCE_DELAY_MS = 2000;

/**
 * Debouncer - Generic debounce utility for delayed execution
 *
 * Features:
 * - Delays callback execution until no new calls within delay window
 * - Optional max wait time to ensure eventual execution
 * - Supports manual flush and cancel operations
 * - Type-safe generic implementation
 *
 * @typeParam T - Type of value passed to the callback
 * @see Requirements 2.1, 2.2
 *
 * @example
 * ```typescript
 * const debouncer = new Debouncer<string>(
 *   (value) => console.log('Saved:', value),
 *   { delayMs: 2000 }
 * );
 *
 * debouncer.call('draft 1'); // Starts timer
 * debouncer.call('draft 2'); // Resets timer
 * debouncer.call('draft 3'); // Resets timer
 * // After 2 seconds: logs "Saved: draft 3"
 * ```
 */
export class Debouncer<T> {
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private maxWaitTimerId: ReturnType<typeof setTimeout> | null = null;
  private lastValue: T | null = null;
  private firstCallTime: number | null = null;
  private readonly delayMs: number;
  private readonly maxWaitMs: number | undefined;

  /**
   * Create a new Debouncer instance
   *
   * @param callback - Function to call with the debounced value
   * @param options - Debouncer configuration options
   */
  constructor(
    private readonly callback: (value: T) => void,
    options: DebouncerOptions
  ) {
    this.delayMs = options.delayMs;
    this.maxWaitMs = options.maxWaitMs;
  }

  /**
   * Schedule a debounced call with the given value
   *
   * If called multiple times within the delay window, only the last value
   * will be passed to the callback when the timer expires.
   *
   * @param value - Value to pass to the callback
   * @see Requirements 2.1 - Debounce with 2-second delay after last edit
   * @see Requirements 2.2 - Only enqueue final state within debounce window
   */
  call(value: T): void {
    // Store the latest value
    this.lastValue = value;

    // Track first call time for maxWait calculation
    if (this.firstCallTime === null) {
      this.firstCallTime = Date.now();

      // Set up max wait timer if configured
      if (this.maxWaitMs !== undefined) {
        this.maxWaitTimerId = setTimeout(() => {
          this.executeCallback();
        }, this.maxWaitMs);
      }
    }

    // Clear existing debounce timer
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
    }

    // Set new debounce timer
    this.timerId = setTimeout(() => {
      this.executeCallback();
    }, this.delayMs);
  }

  /**
   * Cancel any pending debounced call
   *
   * Clears all timers and resets state without executing the callback.
   */
  cancel(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    if (this.maxWaitTimerId !== null) {
      clearTimeout(this.maxWaitTimerId);
      this.maxWaitTimerId = null;
    }

    this.lastValue = null;
    this.firstCallTime = null;
  }

  /**
   * Immediately execute the pending callback
   *
   * If there is a pending value, executes the callback immediately
   * and clears all timers. Does nothing if no call is pending.
   */
  flush(): void {
    if (this.lastValue !== null) {
      this.executeCallback();
    }
  }

  /**
   * Check if there is a pending debounced call
   *
   * @returns true if a call is pending, false otherwise
   */
  isPending(): boolean {
    return this.timerId !== null;
  }

  /**
   * Get the current pending value (for testing/debugging)
   *
   * @returns The pending value, or null if no call is pending
   */
  getPendingValue(): T | null {
    return this.lastValue;
  }

  /**
   * Execute the callback and reset state
   */
  private executeCallback(): void {
    // Clear timers
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    if (this.maxWaitTimerId !== null) {
      clearTimeout(this.maxWaitTimerId);
      this.maxWaitTimerId = null;
    }

    // Execute callback with last value
    const value = this.lastValue;
    this.lastValue = null;
    this.firstCallTime = null;

    if (value !== null) {
      this.callback(value);
    }
  }
}
